/**
 * GradeService — orchestrates the grading flow per Arch §2.6.
 *
 * V1 shell scope:
 *   - requestGrade builds the per-decision GradeRequest from a
 *     UI-friendly GradeInput.
 *   - Performs ONE attempt against the configured RubricGrader.
 *   - On success returns { status: 'graded', grade } with per-decision
 *     + aggregate time scores stamped client-side.
 *   - On thrown error returns { status: 'terminal', reason: 'api_error' }.
 *   - retryPending() is a no-op stub; PRD §11.3's 3-tier retry +
 *     background backoff queue land in a follow-up slice.
 *
 * The static / dynamic split inside GradeRequest mirrors PRD §11.3
 * prompt-caching strategy (~10K tokens cached, per-call ~$0.0013).
 *
 * Grading granularity: per hero decision. See `src/types/grade.ts` for
 * the data shape.
 */

import type {
  CriterionGrade,
  DecisionGrade,
  DecisionTimeScore,
  GradeInput,
  GradeOutcome,
  GradeRequest,
  HeroActionKind,
  RubricGrade,
} from '@/types/grade';
import { RUBRIC_CRITERIA } from '@/types/grade';
import type { ActionRecord, HandState } from '@/types/handState';
import { cannedRubricGrader as _cannedRubricGrader } from './cannedRubricGrader';
import { openaiAdapter } from './openaiAdapter';
import type { RubricGrader } from './rubricGrader';

// Canned grader retained as fast swap-back for dev (`npm run dev`
// without `wrangler dev` means /api/grade is unreachable). Touch it
// here so the strict-unused build check doesn't complain.
void _cannedRubricGrader;

const RUBRIC_VERSION = 'v1';

// V1 shell — placeholder static context. When the real grader lands, these
// strings get replaced with the actual rubric.md + the strategy slice for
// the session's pot+position combo (pulled from rtp_default_strategy.json).
const PLACEHOLDER_RUBRIC =
  'V1 shell rubric (10 criteria) — see /grading/rubric.md.';
const PLACEHOLDER_STRATEGY_SLICE =
  'V1 shell strategy slice — see /grading/rtp_default_strategy.json.';

export interface GradeService {
  /** Build a GradeRequest from UI input, run the configured grader. */
  requestGrade(input: GradeInput): Promise<GradeOutcome>;
  /** V1 shell: no-op. Reserved for the §11.3 background retry queue. */
  retryPending(): Promise<void>;
}

class V1GradeService implements GradeService {
  constructor(private readonly grader: RubricGrader = openaiAdapter) {}

  async requestGrade(input: GradeInput): Promise<GradeOutcome> {
    const request = buildRequest(input);
    try {
      const grade = await this.grader.grade(request);
      // Stamp objectively-computed time scores. The LLM never computes
      // timing — pure math from ActionRecord.decisionElapsedMs against
      // session.config.timerSeconds.
      const stamped = stampTimeScores(grade, input);
      // Hard guard: for any decision whose transcript was empty, force
      // every non-NA criterion to MISSING. The system prompt asks for
      // this, but LLMs occasionally hallucinate — this is the floor.
      const guarded = enforceEmptyTranscriptRule(stamped, input);
      return { status: 'graded', grade: guarded };
    } catch (err) {
      console.error('[GradeService] grader threw:', err);
      const message = err instanceof Error ? err.message : String(err);
      return { status: 'terminal', reason: 'api_error', message };
    }
  }

  async retryPending(): Promise<void> {
    // V1 shell: nothing queued. §11.3 retry pipeline is a follow-up.
  }
}

// =============================================================================
// Request building
// =============================================================================

function buildRequest(input: GradeInput): GradeRequest {
  const { config, state, heroActions } = input;
  // Postflop-only. Preflop is animated by the engine today and never
  // surfaces a hero decision, but if that ever changes a preflop entry
  // would violate the JSON schema's street enum (flop/turn/river) and
  // OpenAI would reject the response. Filter defensively.
  const postflopHero = heroActions.filter(
    (rec) =>
      rec.street === 'flop' ||
      rec.street === 'turn' ||
      rec.street === 'river',
  );
  return {
    rubricVersion: RUBRIC_VERSION,
    staticContext: {
      rubric: PLACEHOLDER_RUBRIC,
      strategySlice: PLACEHOLDER_STRATEGY_SLICE,
    },
    dynamicContext: {
      gameState: {
        potType: config.potType,
        heroPosition: state.heroPosition,
        archetype: config.archetype,
        sessionFocus: config.sessionFocus,
        handId: state.handId,
        heroCards: state.heroCards,
        villainCards: state.villainCards,
        board: state.board,
        pot: state.pot,
        stacks: state.stacks,
        terminalReason: state.terminalReason,
        history: state.history.map((rec) => ({
          street: rec.street,
          actor: rec.actor,
          action: rec.action,
          chipsCommitted: rec.chipsCommitted,
          potAfter: rec.potAfter,
        })),
      },
      decisions: postflopHero.map((rec) => buildDecisionPayload(rec, state)),
    },
  };
}

/**
 * Build the per-decision payload the LLM sees. Keys are snake_case to
 * match the system prompt and the response schema verbatim — the model
 * is told to echo back `action_index`, `street`, and `action_kind`, and
 * to gate Bluff-Catch-Checklist applicability off `facing_bet`.
 *
 * Empty / whitespace-only transcripts are replaced with an unambiguous
 * marker string so the LLM can't gloss over them and hallucinate
 * verbalization. The empty-transcript rule in the system prompt
 * instructs the model to mark every Active criterion MISSING in that
 * case.
 */
function buildDecisionPayload(rec: ActionRecord, state: HandState) {
  const raw = rec.transcript ?? '';
  const isEmpty = raw.trim().length === 0;
  return {
    action_index: rec.index,
    street: rec.street,
    action_kind: rec.action.kind as HeroActionKind,
    facing_bet: heroWasFacingBet(rec, state),
    transcript: isEmpty
      ? '[EMPTY — no audio was captured or transcribed for this decision]'
      : raw,
    transcript_is_empty: isEmpty,
  };
}

/**
 * True if at the moment of this hero action, the previous in-history
 * action was a non-check by villain (i.e., hero is responding to a
 * bet/raise/all-in). Cheap heuristic; the engine has fuller state but
 * we don't need it for grading.
 */
function heroWasFacingBet(rec: ActionRecord, state: HandState): boolean {
  if (rec.index === 0) return false;
  const prev = state.history[rec.index - 1];
  if (!prev || prev.actor !== 'villain') return false;
  const k = prev.action.kind;
  return k === 'bet' || k === 'raise' || k === 'allin';
}

// =============================================================================
// Time-score computation
// =============================================================================

/**
 * Stamp per-decision + aggregate time scores onto the grade. The LLM
 * never sees timing data; the math is purely from
 * ActionRecord.decisionElapsedMs vs session.config.timerSeconds.
 *
 * When `timerSeconds` is null (no-timer preset), per-decision timeScores
 * are omitted and the aggregate stays at zeros — the UI shows "—".
 */
function stampTimeScores(
  grade: RubricGrade,
  input: GradeInput,
): RubricGrade {
  const { config, state, heroActions } = input;
  const timerSec = config.timerSeconds;
  const timerMs = timerSec === null ? null : timerSec * 1000;

  // Postflop hero actions in history order — the same set we shipped to
  // the LLM in buildRequest. We rely on the LLM to echo our action_index,
  // but fall back to positional matching if it ever drops it.
  const postflopHero = heroActions.filter(
    (rec) =>
      rec.street === 'flop' ||
      rec.street === 'turn' ||
      rec.street === 'river',
  );

  // Index hero actions by their canonical history index.
  const heroByIdx = new Map<number, ActionRecord>(
    heroActions.map((rec) => [rec.index, rec]),
  );
  // Belt + suspenders — also pick up any hero records we missed.
  state.history.forEach((rec) => {
    if (rec.actor === 'hero' && !heroByIdx.has(rec.index)) {
      heroByIdx.set(rec.index, rec);
    }
  });

  let aggOnTime = 0;
  let aggTotal = 0;
  let aggSec = 0;

  const decisions: DecisionGrade[] = grade.decisions.map((d, i) => {
    // Prefer matching by the action_index the LLM echoed back; if that
    // doesn't resolve (LLM dropped/garbled the field), fall back to the
    // i-th postflop hero action so transcript + time still align.
    const rec = heroByIdx.get(d.actionIndex) ?? postflopHero[i];
    // Also normalize the decision's actionIndex to the real record's
    // index so downstream UI lookups (transcript display) always work.
    const fixedDecision: DecisionGrade =
      rec && rec.index !== d.actionIndex
        ? { ...d, actionIndex: rec.index }
        : d;

    if (
      !rec ||
      timerMs === null ||
      rec.decisionElapsedMs === undefined
    ) {
      return { ...fixedDecision, timeScore: undefined };
    }
    const ts: DecisionTimeScore = {
      seconds: Math.round(rec.decisionElapsedMs / 1000),
      onTime: rec.decisionElapsedMs <= timerMs,
    };
    aggTotal += 1;
    aggSec += ts.seconds;
    if (ts.onTime) aggOnTime += 1;
    return { ...fixedDecision, timeScore: ts };
  });

  return {
    ...grade,
    decisions,
    timeScore: {
      onTime: aggOnTime,
      total: aggTotal,
      avgSeconds: aggTotal > 0 ? Math.round(aggSec / aggTotal) : 0,
    },
  };
}

// =============================================================================
// Empty-transcript enforcement
// =============================================================================

const EMPTY_TRANSCRIPT_FEEDBACK =
  'No audio captured for this decision — nothing to grade in the transcript.';

/**
 * Belt-and-suspenders client-side guard. For each decision whose source
 * ActionRecord has an empty (or whitespace-only) transcript, rewrite any
 * non-NA criterion verdict to MISSING. The system prompt instructs the
 * LLM to do this; this guard ensures correctness even if the model
 * hallucinates a PASS/PARTIAL based on game-state context.
 *
 * Criteria whose Action Trigger is NOT met (verdict === 'NA') are left
 * alone — NA means "trigger not met," not "no transcript content." So a
 * non-betting decision keeps Target/Inverse Sizing as NA, etc.
 *
 * Default's prescribed-default block is left alone — that's evaluated
 * from action_kind vs the rubric, not from transcript content.
 */
function enforceEmptyTranscriptRule(
  grade: RubricGrade,
  input: GradeInput,
): RubricGrade {
  const { state } = input;
  const recByIdx = new Map<number, { transcript?: string }>();
  state.history.forEach((rec) => {
    if (rec.actor === 'hero') recByIdx.set(rec.index, rec);
  });

  const decisions: DecisionGrade[] = grade.decisions.map((d) => {
    const rec = recByIdx.get(d.actionIndex);
    const transcript = rec?.transcript ?? '';
    if (transcript.trim().length > 0) return d;
    // Empty transcript — rewrite every non-NA criterion to MISSING.
    const fixedCriteria = {} as DecisionGrade['criteria'];
    for (const name of RUBRIC_CRITERIA) {
      const existing: CriterionGrade | undefined = d.criteria[name];
      if (!existing || existing.verdict === 'NA') {
        fixedCriteria[name] = existing ?? {
          verdict: 'NA',
          feedback: 'Trigger not met on this decision.',
        };
        continue;
      }
      fixedCriteria[name] = {
        verdict: 'MISSING',
        feedback: EMPTY_TRANSCRIPT_FEEDBACK,
      };
    }
    return { ...d, criteria: fixedCriteria };
  });

  return { ...grade, decisions };
}

// =============================================================================
// Singleton
// =============================================================================

export const gradeService: GradeService = new V1GradeService();
